import { FC, useState } from "react";
import { Check, ChevronDownIcon, UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Dropdown from "../../components/dropdown";
import { Agent } from "types";
import { AgentStatus } from "constants/agents";
import { useNavigate } from "react-router-dom";
import { logout } from "auth/tokenStorage";
import { useAgentContext } from "../context/AgentContext";
import { useAuthContext } from "auth/AuthProvider";
import { useAssistantRuntime } from "@assistant-ui/react";
import WorkspacesDropdown from "../../components/navbar/WorkspacesDropdown";
import { useTranslation } from "react-i18next";

export const ChatNavbar: FC = () => {
  const { t } = useTranslation();
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const {
    selectedAgent,
    setSelectedAgent,
    availableAgents,
    isLoadingAgents,
    agentsError,
  } = useAgentContext();
  const { hasFullControl } = useAuthContext();
  const navigate = useNavigate();
  const runtime = useAssistantRuntime();

  const activeAgents = availableAgents.filter(
    (a) => !a.is_deleted && !a.is_archived
  );

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    setAgentDropdownOpen(false);
    navigate(`/chat/${agent.id}`);
    runtime.switchToNewThread();
  };

  return (
    <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
      {/* Agent Dropdown */}
      <div className="flex items-center gap-2">
        <Dropdown
          button={
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-3 py-2"
            >
              {selectedAgent ? (
                <>
                  {selectedAgent.icon_uri ? (
                    <img
                      src={selectedAgent.icon_uri}
                      alt={selectedAgent.name}
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                      <span className="text-xs font-semibold text-primary-foreground">
                        {selectedAgent.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-sm font-medium">
                    {selectedAgent.name}
                  </span>
                </>
              ) : (
                <span className="text-sm font-medium">
                  {t("chat.selectAgent")}
                </span>
              )}
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          }
          classNames="bg-background border border-border rounded-lg shadow-lg p-1 min-w-48 top-full left-0 mt-1"
          isOpen={agentDropdownOpen}
          setIsOpen={setAgentDropdownOpen}
        >
          <div className="max-h-[500px] overflow-y-auto py-1 [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar]:w-1">
            {isLoadingAgents ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            ) : agentsError ? (
              <div className="px-3 py-2 text-sm text-destructive">
                {agentsError}
              </div>
            ) : activeAgents.length > 0 ? (
              activeAgents.map((agent) => {
                const isAgentReady =
                  agent.status === AgentStatus.READY && !agent.is_archived;
                return (
                  <div
                    key={agent.id}
                    onClick={() => isAgentReady && handleAgentSelect(agent)}
                    className={`flex items-center justify-between gap-2 rounded px-3 py-2 ${
                      isAgentReady
                        ? "cursor-pointer hover:bg-accent"
                        : "cursor-not-allowed opacity-50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {agent.icon_uri ? (
                        <img
                          src={agent.icon_uri}
                          alt={agent.name}
                          className="h-6 w-6 rounded-full"
                        />
                      ) : (
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                          <span className="text-xs font-semibold text-primary-foreground">
                            {agent.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className="text-sm">{agent.name}</span>
                    </div>
                    {selectedAgent?.id === agent.id && (
                      <Check className="h-4 w-4" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t("chat.noAgentsAvailable")}
              </div>
            )}
          </div>
        </Dropdown>
      </div>

      {/* Right side - Upload and User Menu */}
      <div className="flex items-center gap-2">
        <WorkspacesDropdown skipRedirect={true} showViewAll={false} />
        {/* Upload Button */}
        {/* <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <UploadIcon className="h-4 w-4" />
        </Button> */}

        {/* User Menu */}
        <Dropdown
          button={
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2 py-1"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                <UserIcon className="h-3 w-3 text-muted-foreground" />
              </div>
            </Button>
          }
          classNames="bg-background border border-border rounded-lg shadow-lg p-1 min-w-48 top-full right-0 mt-1"
          isOpen={userDropdownOpen}
          setIsOpen={setUserDropdownOpen}
          wrapperClassName="relative"
        >
          <div className="py-1">
            {hasFullControl() && (
              <div
                className="cursor-pointer rounded px-3 py-2 text-sm hover:bg-accent"
                onClick={() => navigate("/dashboard/agents")}
              >
                {t("chat.management")}
              </div>
            )}
            <div
              className="cursor-pointer rounded px-3 py-2 text-sm text-destructive hover:bg-accent"
              onClick={logout}
            >
              {t("navbar.logout")}
            </div>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
